<script>
    // Replaces native alert(). An OS grey box in a product this considered
    // reads as a crash, and alert() blocks the whole page.
    //
    //   <Banner tone="danger" title="We couldn't read that photo">
    //     Try again in better light, or upload one from your camera roll.
    //   </Banner>
    //
    // Set `dismissible` for anything the user can safely ignore. Errors that
    // block the flow should not be dismissible.
    //
    // `visible` is read, never written. Dismissal is tracked separately, so a
    // parent that passes visible={hasError} does not silently end up out of
    // sync with what is on screen. Listen for on:dismiss, or use bind:visible
    // if the parent wants to own the state outright.

    import { createEventDispatcher } from 'svelte';

    export let tone = 'info';   // info | success | caution | danger
    export let title = null;
    export let dismissible = false;
    export let visible = true;

    const dispatch = createEventDispatcher();

    let dismissed = false;
    let wasVisible = visible;

    // Re-showing (visible false -> true) clears a previous dismissal, so the
    // same Banner instance can be reused for a second error.
    $: {
        if (visible && !wasVisible) dismissed = false;
        wasVisible = visible;
    }

    $: shown = visible && !dismissed;

    function dismiss() {
        dismissed = true;
        dispatch('dismiss');
    }

    export function reset() {
        dismissed = false;
    }
</script>

<style>
    .banner {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-4);
        border: 1px solid;
        border-left-width: 3px;
        border-radius: var(--radius-0);
        font-size: var(--text-sm);
        line-height: var(--leading-normal);
    }

    .info    { background: var(--surface-2);  border-color: var(--border); border-left-color: var(--sage); }
    .success { background: var(--success-bg); border-color: var(--border); border-left-color: var(--success); }
    .caution { background: var(--caution-bg); border-color: var(--border); border-left-color: var(--caution); }
    .danger  { background: var(--danger-bg);  border-color: var(--border); border-left-color: var(--danger); }

    .body { flex: 1; min-width: 0; color: var(--text); }

    .title {
        display: block;
        font-weight: var(--weight-semibold);
        margin-bottom: var(--space-1);
    }

    .message { color: var(--text-muted); }

    .close {
        flex: none;
        background: none;
        border: none;
        padding: 0;
        margin: 0;
        line-height: 1;
        font-size: var(--text-lg);
        color: var(--text-muted);
        cursor: pointer;
        transition: color var(--transition);
    }
    .close:hover { color: var(--cream); }
</style>

{#if shown}
    <div class="banner {tone}" role={tone === 'danger' ? 'alert' : 'status'}>
        <div class="body">
            {#if title}<strong class="title">{title}</strong>{/if}
            <div class="message"><slot /></div>
        </div>
        {#if dismissible}
            <button class="close" type="button" on:click={dismiss}>
                <span aria-hidden="true">&times;</span>
                <span class="u-visually-hidden">Dismiss</span>
            </button>
        {/if}
    </div>
{/if}
